import { useHandler } from "@redotech/react-util/hook";
import { remintAllStateUuids } from "@redotech/redo-return-ui/flows/remint-state-uuids";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Edge, Node, ReactFlowInstance } from "@xyflow/react";
import { useCallback, useRef, useState } from "react";
import { NodeData } from "./components/flow-step-node";
import {
  removeStepConnection,
  updateStepConnection,
} from "./utils/palette/update-step-connection";

interface ClipboardData {
  nodes: Node[];
  edges: Edge[];
}

function hasNodeData(node: Node): node is Node<NodeData> {
  return (
    node.data !== undefined &&
    typeof node.data === "object" &&
    "step" in node.data &&
    "stepId" in node.data
  );
}

export function useFlowCopyPaste(
  reactFlowInstance: ReactFlowInstance | null,
  steps: ReadonlyMap<StepId, Readonly<Step>>,
  userNodePositions: Map<string, { x: number; y: number }>,
  gridSize: number,
  onUpdate: (updates: {
    steps: Map<StepId, Step>;
    userNodePositions: Map<string, { x: number; y: number }>;
  }) => void,
  onModified: () => void,
) {
  const clipboardRef = useRef<ClipboardData | null>(null);
  const [pasteCount, setPasteCount] = useState(0);

  const selectedNodeIdsRef = useRef<Set<string>>(new Set());

  const handleCopy = useCallback(() => {
    if (!reactFlowInstance) {
      return;
    }

    const selectedNodes = reactFlowInstance
      .getNodes()
      .filter((n) => n.selected);
    const selectedNodeIds = new Set(selectedNodes.map((n) => n.id));

    const selectedEdges = reactFlowInstance
      .getEdges()
      .filter(
        (e) =>
          e.selected ||
          (selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target)),
      );

    clipboardRef.current = { nodes: selectedNodes, edges: selectedEdges };

    setPasteCount(0);
  }, [reactFlowInstance]);

  const handlePaste = useCallback(() => {
    if (!reactFlowInstance || !clipboardRef.current) {
      return;
    }
    const NODE_OFFSET = gridSize * 2;

    const { nodes: copiedNodes } = clipboardRef.current;

    if (!copiedNodes.length) {
      return;
    }

    const cumulativeOffset = NODE_OFFSET * (pasteCount + 1);

    const existingIds = Array.from(steps.keys())
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;

    const idMap = new Map<string, string>();
    let nextId = maxId + 1;

    const getNewId = (oldId: string) => {
      if (!idMap.has(oldId)) {
        if (oldId === "start") {
          return oldId;
        }
        const newId = String(nextId);
        idMap.set(oldId, newId);
        nextId++;
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return idMap.get(oldId)!;
    };

    const newSteps = new Map(steps);
    const newPositions = new Map(userNodePositions);

    const pastedNodes = copiedNodes
      .map((node) => {
        if (!hasNodeData(node)) {
          return null;
        }

        const oldId = node.id;
        const newId = getNewId(oldId);

        const originalStep = node.data.step;

        let clonedStep = originalStep;
        const downstreams = originalStep.downstream();

        for (const [i, downstream] of downstreams.entries()) {
          if (
            downstream.id &&
            copiedNodes.some((n) => n.id === downstream.id)
          ) {
            const newDownstreamId = getNewId(downstream.id);
            clonedStep = updateStepConnection(clonedStep, i, newDownstreamId);
          } else {
            clonedStep = removeStepConnection(clonedStep, downstream.id || "");
          }
        }

        newSteps.set(newId, remintAllStateUuids(clonedStep));

        const newPosition = {
          x: node.position.x + cumulativeOffset,
          y: node.position.y + cumulativeOffset,
        };
        newPositions.set(newId, newPosition);

        return { ...node, id: newId, position: newPosition, selected: true };
      })
      .filter((node): node is NonNullable<typeof node> => node !== null);

    const pastedNodeIds = new Set(pastedNodes.map((n) => n.id));

    onUpdate({ steps: newSteps, userNodePositions: newPositions });
    onModified();

    selectedNodeIdsRef.current = pastedNodeIds;

    setPasteCount((count) => count + 1);
  }, [
    reactFlowInstance,
    pasteCount,
    steps,
    userNodePositions,
    gridSize,
    onUpdate,
    onModified,
  ]);

  const getAndClearSelectedNodeIds = useHandler(() => {
    const ids = selectedNodeIdsRef.current;
    selectedNodeIdsRef.current = new Set();
    return ids;
  });

  const clearSelectedNodeIds = useHandler(() => {
    selectedNodeIdsRef.current = new Set();
  });

  return {
    handleCopy,
    handlePaste,
    selectedNodeIdsRef,
    getAndClearSelectedNodeIds,
    clearSelectedNodeIds,
  };
}
